import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Bitcoin, Shield, CheckCircle2, ArrowRight, 
  Search, Lock, Eye, AlertTriangle, Zap, Clock, 
  TrendingUp, Users, DollarSign, FileCheck, Briefcase,
  ChevronRight
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const HowItWorksPage = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(1);

  const processSteps = [
    {
      step: 1,
      title: 'Identify the Asset',
      icon: Search,
      description: 'AssumeRate flags assumable, distressed, or at-risk CRE loans in your portfolio—or accepts new originations from BTC-holding buyers.',
      details: [
        'Portfolio analysis identifies at-risk loans',
        'Distressed asset matching with qualified buyers',
        'Pre-screening of Bitcoin-holding borrowers',
        'Integration with existing loan management systems'
      ]
    },
    {
      step: 2,
      title: 'Bitcoin Collateral Posted',
      icon: Bitcoin,
      description: 'Borrower pledges Bitcoin held at institutional custodian (BitGo, Coinbase Prime, Fidelity Digital Assets) with 150–200% overcollateralization.',
      details: [
        'Institutional-grade custody (SOC 2 Type II)',
        '95-100% insurance coverage',
        'Lender named as loss payee',
        'Real-time collateral verification'
      ]
    },
    {
      step: 3,
      title: 'Dual-Layer Protection',
      icon: Shield,
      description: 'Bank holds both real estate + Bitcoin collateral. AssumeRate monitors 24/7 with automated margin calls before losses occur.',
      details: [
        '24/7 Bitcoin valuation engine',
        'Automated margin call triggers',
        '87% margin calls cured within 48 hours',
        'EWMA volatility recalculation daily'
      ]
    },
    {
      step: 4,
      title: 'Loan Resolved, Risk Gone',
      icon: CheckCircle2,
      description: 'Distressed loan becomes a performing asset. Bank recycles capital, avoids foreclosure costs, and earns fee revenue from the platform.',
      details: [
        '60% faster workout timeline',
        '$50K-$150K saved per avoided foreclosure',
        'Faster capital recycling into new originations',
        'New fee revenue stream from platform'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#F7F7F7]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-block bg-[#E8F4FD] text-[#006AFF] px-4 py-2 rounded-full text-sm font-medium mb-6">
            B2B CRE Portfolio Health Platform
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#2A2A33] mb-6">
            How <span className="text-[#006AFF]">AssumeRate</span> Works
          </h1>
          <p className="text-lg text-[#6B6B76] max-w-3xl mx-auto mb-8">
            A B2B platform that helps banks resolve distressed CRE loans faster—using Bitcoin as supplemental collateral.
          </p>
          
          {/* Key Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-12">
            <div className="bg-[#F7F7F7] rounded-xl p-4">
              <div className="text-3xl font-bold text-[#006AFF]">$930B+</div>
              <div className="text-sm text-[#6B6B76]">CRE Maturing 2026</div>
            </div>
            <div className="bg-[#F7F7F7] rounded-xl p-4">
              <div className="text-3xl font-bold text-[#006AFF]">154%</div>
              <div className="text-sm text-[#6B6B76]">Avg Collateral Coverage</div>
            </div>
            <div className="bg-[#F7F7F7] rounded-xl p-4">
              <div className="text-3xl font-bold text-[#006AFF]">60%</div>
              <div className="text-sm text-[#6B6B76]">Faster Workout Timeline</div>
            </div>
            <div className="bg-[#F7F7F7] rounded-xl p-4">
              <div className="text-3xl font-bold text-[#006AFF]">87%</div>
              <div className="text-sm text-[#6B6B76]">Margin Call Cure Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Process Workflow */}
      <section className="py-16 px-4 bg-[#F7F7F7]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-[#2A2A33] text-center mb-4">The 4-Step Process</h2>
          <p className="text-[#6B6B76] text-center mb-12 max-w-2xl mx-auto">
            Click each step to learn more about our streamlined workflow
          </p>
          
          {/* Step Navigation */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {processSteps.map((step) => (
              <button
                key={step.step}
                onClick={() => setActiveStep(step.step)}
                className={`flex items-center px-6 py-3 rounded-xl transition-all ${
                  activeStep === step.step
                    ? 'bg-[#006AFF] text-white shadow-lg'
                    : 'bg-white text-[#2A2A33] border border-gray-200 hover:border-[#006AFF]'
                }`}
                data-testid={`step-${step.step}-btn`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                  activeStep === step.step ? 'bg-white/20' : 'bg-[#E8F4FD]'
                }`}>
                  <step.icon className={`h-4 w-4 ${activeStep === step.step ? 'text-white' : 'text-[#006AFF]'}`} />
                </div>
                <span className="font-medium">{step.title}</span>
                {step.step < 4 && <ChevronRight className="h-4 w-4 ml-2 opacity-50" />}
              </button>
            ))}
          </div>

          {/* Active Step Detail */}
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-4xl mx-auto">
            {processSteps.map((step) => (
              step.step === activeStep && (
                <div key={step.step} className="animate-fadeIn">
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 bg-[#E8F4FD] rounded-2xl flex items-center justify-center mr-4">
                      <step.icon className="h-8 w-8 text-[#006AFF]" />
                    </div>
                    <div>
                      <div className="text-sm text-[#006AFF] font-medium">Step {step.step}</div>
                      <h3 className="text-2xl font-bold text-[#2A2A33]">{step.title}</h3>
                    </div>
                  </div>
                  <p className="text-lg text-[#6B6B76] mb-6">{step.description}</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    {step.details.map((detail, idx) => (
                      <div key={idx} className="flex items-start">
                        <CheckCircle2 className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-[#2A2A33]">{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-[#2A2A33] text-center mb-4">
            Why Bitcoin Collateral Is Superior
          </h2>
          <p className="text-[#6B6B76] text-center mb-12 max-w-2xl mx-auto">
            AssumeRate delivers measurably better risk protection on every dimension that matters to credit committees.
          </p>
          
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F7F7F7]">
                  <th className="text-left p-5 text-[#2A2A33] font-semibold">Feature</th>
                  <th className="text-left p-5 text-[#6B6B76] font-semibold">Traditional CRE Loan</th>
                  <th className="text-left p-5 text-[#006AFF] font-semibold">AssumeRate + Bitcoin</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: 'Collateral Coverage', traditional: '~80% LTV (real estate only)', assumerate: '154% avg (BTC + real estate)' },
                  { feature: 'Liquidation Speed', traditional: '3–12 months foreclosure', assumerate: '24–48 hrs (crypto exchange)' },
                  { feature: 'Monitoring', traditional: 'Quarterly / reactive', assumerate: '24/7 real-time + auto alerts' },
                  { feature: 'Margin Call Cure Rate', traditional: 'N/A', assumerate: '87% cured within 48 hours' },
                  { feature: 'Workout Timeline', traditional: '90–120+ days', assumerate: '45–60 days (60% faster)' },
                  { feature: 'Capital Access for Buyers', traditional: 'Limited (tight credit)', assumerate: 'Bitcoin holders = new buyer pool' },
                ].map((row, idx) => (
                  <tr key={idx} className={`border-t border-gray-200 ${idx % 2 === 1 ? 'bg-[#FAFAFA]' : ''}`}>
                    <td className="p-5 font-medium text-[#2A2A33]">{row.feature}</td>
                    <td className="p-5 text-red-500">✗ {row.traditional}</td>
                    <td className="p-5 text-green-600 font-medium">✔ {row.assumerate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Benefits for All Parties */}
      <section className="py-16 px-4 bg-[#F7F7F7]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-[#2A2A33] text-center mb-4">
            Every Party Wins — Especially Your Institution
          </h2>
          <p className="text-[#6B6B76] text-center mb-12">
            A true win-win-win scenario
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Banks */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200">
              <div className="w-14 h-14 bg-[#E8F4FD] rounded-xl flex items-center justify-center mb-6">
                <Building2 className="h-7 w-7 text-[#006AFF]" />
              </div>
              <h3 className="text-xl font-bold text-[#2A2A33] mb-4">🏦 Your Bank / Lender</h3>
              <ul className="space-y-3">
                {[
                  'Reduce distressed CRE on balance sheet',
                  '154% avg collateral coverage vs. 80% LTV',
                  '60% faster workout — free capital sooner',
                  'Avoid costly foreclosure & legal expense',
                  'New fee revenue stream from platform',
                  'Competitive differentiation in CRE market'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start text-[#6B6B76]">
                    <span className="text-[#006AFF] mr-2">▸</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Bitcoin Buyers */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200">
              <div className="w-14 h-14 bg-[#FEF3C7] rounded-xl flex items-center justify-center mb-6">
                <Bitcoin className="h-7 w-7 text-[#F59E0B]" />
              </div>
              <h3 className="text-xl font-bold text-[#2A2A33] mb-4">₿ Bitcoin Buyers</h3>
              <ul className="space-y-3">
                {[
                  'Access CRE capital without selling BTC',
                  'No capital gains tax trigger on BTC',
                  'Assume existing low-rate mortgages',
                  'Retain Bitcoin upside while buying property',
                  'Expand buyer universe for distressed assets'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start text-[#6B6B76]">
                    <span className="text-[#F59E0B] mr-2">▸</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Distressed Sellers */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-200">
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                <Users className="h-7 w-7 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-[#2A2A33] mb-4">🏗 Distressed Sellers</h3>
              <ul className="space-y-3">
                {[
                  'Larger buyer pool = better sale price',
                  'Avoid foreclosure & credit damage',
                  'Faster closing (45–60 days vs. 120+)',
                  'More negotiating power with buyers',
                  'Preserve relationships with lenders'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start text-[#6B6B76]">
                    <span className="text-green-600 mr-2">▸</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Four Use Cases */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-[#2A2A33] text-center mb-4">
            Four Ways AssumeRate Heals Your CRE Portfolio
          </h2>
          <p className="text-[#6B6B76] text-center mb-12">
            Tailored solutions for different distressed scenarios
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                icon: Building2,
                emoji: '🏢',
                title: 'Distressed Office Loans',
                problem: '11.76% delinquency. Structural oversupply. Foreclosure proceeds don\'t cover exposure.',
                solution: 'Bitcoin buyers assume existing mortgages at below-market rates. Bank converts NPL to performing loan.'
              },
              {
                icon: TrendingUp,
                emoji: '🔄',
                title: 'Loan Assumptions (FHA/VA/USDA)',
                problem: 'Below-market-rate mortgages are valuable but hard to transfer to new buyers who lack liquidity.',
                solution: 'BTC holders assume existing loans without selling crypto. Bank retains solid loan, gains new qualified borrower.'
              },
              {
                icon: AlertTriangle,
                emoji: '🚨',
                title: 'Pre-Foreclosure Workout',
                problem: 'Existing borrower delinquent but has Bitcoin holdings. Foreclosure costs bank $50K–$150K+.',
                solution: 'Borrower pledges BTC to cure delinquency. Loan stays performing. 87% cure rate within 48 hours.'
              },
              {
                icon: Briefcase,
                emoji: '🏗',
                title: 'Bridge / Value-Add Financing',
                problem: 'Distressed assets need stabilization capital. Hard money is expensive; bank can\'t make new CRE loans.',
                solution: 'BTC-backed bridge loan at competitive rates during lease-up. Enables value-add strategies and portfolio growth.'
              }
            ].map((useCase, idx) => (
              <div key={idx} className="bg-[#F7F7F7] rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="flex items-center mb-4">
                  <span className="text-3xl mr-3">{useCase.emoji}</span>
                  <h3 className="text-xl font-bold text-[#2A2A33]">{useCase.title}</h3>
                </div>
                <div className="mb-4">
                  <div className="text-sm font-semibold text-red-500 mb-1">PROBLEM:</div>
                  <p className="text-[#6B6B76] text-sm">{useCase.problem}</p>
                </div>
                <div>
                  <div className="text-sm font-semibold text-green-600 mb-1">✔ SOLUTION:</div>
                  <p className="text-[#2A2A33] text-sm">{useCase.solution}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Risk Framework */}
      <section className="py-16 px-4 bg-[#2A2A33]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-4">
            Built for Credit Committees — Not Crypto Hype
          </h2>
          <p className="text-gray-400 text-center mb-12">
            Every risk is anticipated, quantified, and automatically managed
          </p>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Collateral Framework */}
            <div className="bg-white/10 backdrop-blur rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <Shield className="h-6 w-6 text-[#006AFF] mr-3" />
                Collateral Framework
              </h3>
              <div className="space-y-4">
                {[
                  { label: 'Volatility Haircut', value: '30–45%', desc: '2.326× 30-day historical volatility (99% VaR)' },
                  { label: 'Custody Haircut', value: '5–10%', desc: 'Based on custodian SOC 2, insurance coverage' },
                  { label: 'Liquidity Haircut', value: '0–5%', desc: 'Position size vs. daily exchange volume' },
                  { label: 'Aggregate Haircut', value: '40–65%', desc: 'Combined, recalculated daily via EWMA' }
                ].map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                    <div>
                      <div className="text-white font-medium">{item.label}</div>
                      <div className="text-gray-400 text-xs">{item.desc}</div>
                    </div>
                    <div className="text-[#006AFF] font-bold text-lg">{item.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Automated Margin System */}
            <div className="bg-white/10 backdrop-blur rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <Zap className="h-6 w-6 text-[#F59E0B] mr-3" />
                Automated Margin System
              </h3>
              <div className="space-y-4">
                {[
                  { level: '140%', icon: '⚠', label: 'WARNING', color: 'yellow', desc: 'Email & dashboard alert. No action required.' },
                  { level: '110%', icon: '🔔', label: 'MARGIN CALL', color: 'orange', desc: 'Formal 48-hour cure window. Must add BTC or pay down.' },
                  { level: '100%', icon: '⚡', label: 'LIQUIDATION', color: 'red', desc: 'Orderly BTC sale on exchanges within 24-48 hrs.' },
                  { level: 'Backstop', icon: '🏠', label: 'REAL ESTATE', color: 'blue', desc: 'Property foreclosure only if BTC proceeds insufficient.' }
                ].map((item, idx) => (
                  <div key={idx} className={`p-3 rounded-lg border-l-4 ${
                    item.color === 'yellow' ? 'bg-yellow-500/10 border-yellow-500' :
                    item.color === 'orange' ? 'bg-orange-500/10 border-orange-500' :
                    item.color === 'red' ? 'bg-red-500/10 border-red-500' :
                    'bg-blue-500/10 border-blue-500'
                  }`}>
                    <div className="flex items-center mb-1">
                      <span className="text-lg mr-2">{item.icon}</span>
                      <span className="text-white font-bold">{item.level}</span>
                      <span className={`ml-2 text-sm font-medium ${
                        item.color === 'yellow' ? 'text-yellow-400' :
                        item.color === 'orange' ? 'text-orange-400' :
                        item.color === 'red' ? 'text-red-400' :
                        'text-blue-400'
                      }`}>{item.label}</span>
                    </div>
                    <div className="text-gray-400 text-sm">{item.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-[#006AFF]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to See How It Works With Real Numbers?
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            View our example transaction using the Oxbridge Square property
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/example-transaction')}
              className="bg-white text-[#006AFF] hover:bg-gray-100 px-8 py-6 text-lg rounded-lg shadow-lg font-semibold"
              data-testid="view-example-btn"
            >
              View Example Transaction
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              onClick={() => navigate('/partnership')}
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#006AFF] px-8 py-6 text-lg rounded-lg"
            >
              Partnership Program
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HowItWorksPage;
